import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form943Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 943 Instructions: Report Agricultural Employment Taxes"
          description="Form 943 Instructions for 2025 Learn how to report agricultural wages, taxes, and credits, with step-by-step guidance for accurate filing and compliance"
          keywords=""
          canonical="resources/employment-tax/form-943-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 943</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-943-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },

                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 943 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}> Form 943</span> is
                        filed by agricultural employers to the IRS. It reports
                        wages paid to farmworkers, federal income tax withheld,
                        and employee & employers' share of{' '}
                        <Link
                          href="/resources/employment-tax/fica-tax/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          FICA
                        </Link>{' '}
                        taxes.
                      </span>
                    </li>
                    <li>
                      <span>
                        <Link
                          href="/resources/employment-tax/form-943-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>{' '}
                        : Form 943 is filed{' '}
                        <span style={{ fontWeight: 600 }}> annually</span> and
                        is generally due by
                        <span style={{ fontWeight: 600 }}> January 31</span> for
                        wages paid during the previous calendar year.
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Failure to file or pay on time may result in{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          IRS penalties and interest
                        </span>{' '}
                        on any unpaid taxes.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b>Form 943</b> is used by employers to report agricultural
                  employment taxes annually. This form reports wages paid to
                  farmworkers and the taxes withheld from their wages,
                  including:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Social Security and Medicare Taxes:</b> Report the
                    employer and employee portions of Social Security and
                    Medicare taxes.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Federal Income Tax Withholding:</b>&nbsp;Report any
                    federal income tax withheld from the employees&apos; wages.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Advance Earned Income Credit (EIC) Payments:</b> If
                    applicable, report any EIC payments made to employees.
                  </li>
                </ol>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to file Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-Step Instructions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who needs to file Form 943?
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Employers who pay wages to one or more farmworkers are
                    subject to federal income tax withholding or Social Security
                    and Medicare taxes must file Form 943.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Once you file Form 943, you must continue to file it
                    annually even if you have no taxes to report, until you file
                    a final return.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Wage Tests:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>$150 Test:</b> Pay an employee $150 or more in a year for
                    farmwork.
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      This applies separately to each farmworker.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Does not include wages paid by other employers.
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    <b> $2,500 Test:</b> The total wages (cash and noncash) paid
                    to all farmworkers is $2,500 or more.
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      If the group test ($2,500) isn&apos;t met, the individual
                      test ($150) still applies, and vice versa.
                    </li>
                  </ul>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Exceptions:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Special rules apply for certain hand-harvest laborers who
                    earn less than $150 annually.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Final Return:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    If you stop paying wages and don&apos;t expect to resume,
                    file a final return. Mark the box above line 1 on the form
                    to indicate no future returns.
                  </li>
                  <li style={{ marginBottom: 12 }}>Attach a statement with:</li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      Name of the person keeping payroll records.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      If sold/transferred: Name, address of the new owner, and
                      sale/transfer date.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      If there is no sale/transfer or if the new owner is
                      unknown, include this information in the statement.
                    </li>
                  </ul>
                </ul>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Tax credits for qualified sick leave, family
                  leave, and COBRA premiums due to the pandemic are reflected on
                  Form 943.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="step"
                >
                  Step-by-Step Instructions:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 1: Basic Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-943-instruction-img.webp" />
                  <source srcSet="/assets/images/form-943-instruction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-instruction-img.png"
                    loading="lazy"
                    alt="Form 943: Enter EIN, Name, Complete address and type of filers"
                    width={760}
                    height={252}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the EIN, Name of your business, trade name (if any),
                    complete address such as number, street, city, state and ZIP
                    code.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If this mailing address is different than the address you
                    used on your previous Form 943, mark the first checkbox on
                    the right side of the form.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Mark the second check box{' '}
                    <span style={{ fontWeight: 600 }}> only</span> if you expect
                    that you will no longer be required to file returns.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Aggregate Return Filers Only:
                    </span>
                    <br /> Choose any{' '}
                    <span style={{ fontWeight: 600 }}>one box</span> that
                    applies:
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      {' '}
                      <span style={{ fontWeight: 600 }}>
                        Section 3504 Agent —
                      </span>{' '}
                      Check this if you are an employer agent who is filing on
                      behalf of clients under section 3504.{' '}
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      {' '}
                      <span style={{ fontWeight: 600 }}>
                        Certified Professional Employer Organization (CPEO) —
                      </span>{' '}
                      check this if a CPEO is filing this return on behalf of
                      the client.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      {' '}
                      <span style={{ fontWeight: 600 }}>
                        Other Third Party —
                      </span>{' '}
                      Check this box, if you are a third-party filer.{' '}
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      If you are not an aggregate filer, leave these boxes
                      blank.{' '}
                    </li>
                  </ul>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 2: Calculate Wages and Taxes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-943-employee-img.webp" />
                  <source srcSet="/assets/images/form-943-employee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-employee-img.png"
                    loading="lazy"
                    alt="Form 943 Lines 1 to 3: Enter number of employees, wages subject to social security, and calculated tax."
                    width={761}
                    height={105}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 1:</b> Enter the number of agricultural employees on
                  your payroll during the pay period, including March 12, 2025.
                  Do not include household employees, employees in nonpay status
                  during the pay period, pensioners, or active members of the
                  U.S. Armed Forces.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 2:</b> Enter the total cash wages, sick pay, and
                  taxable fringe benefits subject to social security taxes that
                  you paid to employees for farmwork during the calendar year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 3:</b> Enter the Social Security tax, 12.4% of the
                  amount on line 2.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-943-medicare-img.webp" />
                  <source srcSet="/assets/images/form-943-medicare-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-medicare-img.png"
                    loading="lazy"
                    alt="Form 943 Lines 4 to 11: Enter Medicare wages, additional Medicare tax, and total taxes. "
                    width={760}
                    height={270}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 4:</b> Total wages paid, including qualified leave.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 5:</b> Multiply line 4 by 2.9%.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 6:</b> Wages subject to Additional Medicare tax (over
                  $200,000).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 7:</b> Multiply line 6 by 0.9%.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 8:</b> Total federal income tax withheld.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 9:</b> Enter the total of the Social Security tax
                  (line 3), Medicare tax (line 5), Additional Medicare Tax
                  withholding (line 7), and federal income tax withheld (line
                  8).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 10:</b> Round fractions and add uncollected sick pay
                  or life insurance payments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 11:</b> Combine lines 9 and 10.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-943-small-tax-img.webp" />
                  <source srcSet="/assets/images/form-943-small-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-small-tax-img.png"
                    loading="lazy"
                    alt="Form 943 Lines 12 to 16e: Enter payroll tax credits, total taxes, deposits, balance due, and refund details. "
                    width={756}
                    height={248}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 12:</b> Enter the amount of the credit from Form
                  8974(Qualified Small Business Payroll Tax Credit for
                  Increasing Research Activities), line 12 or line 17. If you
                  enter an amount on line 12, attach Form 8974
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 13:</b> Enter the result of subtracting line 12 from
                  line 11 on line 13. The amount on line 13 cannot be less than
                  zero.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 14:</b> Enter the total deposits for this year,
                  including any overpayment applied from filing Form 943-X and
                  any overpayment from a previous period applied to this return.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 15:</b> If line 13 is more than line 14, enter the
                  difference on line 15.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 16a:</b> If Line 14 is greater than Line 13, enter the
                  difference on line 16a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 16b:</span> If you
                  have an overpayment, select whether you want the amount
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    applied to your next return
                  </span>{' '}
                  or as a <span style={{ fontWeight: 600 }}>refund</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 16c:</span> Enter the
                  routing number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 16d:</span> Check the
                  account type – checking or savings
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 16e:</span> Enter the
                  account number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-943-ein-business-img.webp" />
                  <source srcSet="/assets/images/form-943-ein-business-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-ein-business-img.png"
                    loading="lazy"
                    alt="Enter the name and EIN "
                    width={738}
                    height={38}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the legal name and EIN of the business.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-943-federal-img.webp" />
                  <source srcSet="/assets/images/form-943-federal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-federal-img.png"
                    loading="lazy"
                    alt="IRS Form 943 Line 17: Enter the monthly summary of federal tax liability "
                    width={760}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Line 17:</b> Enter the amount on line 17 only if you were a
                  monthly schedule depositor for the entire year and your line
                  13 amount is $2,500 or more. The amount on line 17 must match
                  the amount on line 13. If you were a semiweekly scheduled
                  depositor at any point during the year, do not complete line
                  17. Instead, complete Form 943-A.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the total tax withheld for the month of January(17a),
                  February(17b), March(17c), April(17d), May(17e), June(17f),
                  July(17g), August(17h), September(17i), October(17j),
                  November(17k), and December(17l). Enter the total liability on
                  Line 17m.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-943-third-party-img.webp" />
                  <source srcSet="/assets/images/form-943-third-party-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-943-third-party-img.png"
                    loading="lazy"
                    alt="Form 943: Enter the third-party designee details, signature details and Preparer details "
                    width={760}
                    height={528}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Third-party designee:</b> If an employee, a paid tax
                  preparer, or another person wishes to discuss Form 943 with
                  the IRS, select the &quot;Yes&quot; box in the Third-Party
                  Designee section and provide the necessary information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Sign here:</b> To declare, sign, enter the date, and print
                  the name and title.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Paid Preparer:</b> If the preparer was paid to prepare Form
                  943 and is not an employee of the filing entity, they must
                  fill it out this section.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              '& li': { fontSize: 16 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Why should you e-file Form 941 with TaxZerone?
            </Typography>
            <List sx={{ mb: 3, textAlign: 'left', maxWidth: 750, mx: 'auto' }}>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  IRS-authorized e-file service provider
                </ListItemText>
              </ListItem>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  Smart IRS validations to reduce errors in the return
                </ListItemText>
              </ListItem>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  Supports Schedule B & Form 8974 to report any additional tax
                  liabilities and tax credit amounts accurately
                </ListItemText>
              </ListItem>
            </List>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              Simplify your quarterly payroll tax filing with TaxZerone at just{' '}
              <b>$6.99 per return</b>.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions2023`}
              >
                E-file Form 941 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default Form943Instructions;
